import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * Shared Axios client. Consumes the EXISTING backend contract only:
 * cookie auth (crm_token) via withCredentials; no token juggling on web.
 *
 * NOTE: every caller passes a full `/api/...` path, so the base must be the
 * SAME ORIGIN (empty). A non-empty default like '/api' double-prefixes every
 * feature request to `/api/api/...` → 404. Override only via VITE_API_BASE_URL
 * for split-origin deployments (must then NOT end with /api).
 */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── 401 handling ─────────────────────────────────────────────────────────────
// Registered by the app shell so we avoid an import cycle with the auth store.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  unauthorizedHandler = fn;
}

// Requests where a 401 is an expected, handled-in-place outcome (don't trigger
// the global session-clear): the login attempt and the boot session probe.
function isAuthProbe(url?: string): boolean {
  if (!url) return false;
  return url.includes('/auth/login') || url.includes('/auth/me');
}

// ── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // Hook point for correlation ids / per-request headers. Cookie is automatic.
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string }>) => {
    const status = error.response?.status;
    if (status === 401 && !isAuthProbe(error.config?.url)) {
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  },
);

/** Normalize a backend error into a human message ({ error: '...' } shape). */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error ?? error.message ?? fallback;
  }
  return fallback;
}
